import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import StatusBorder from "@/shared/components/ui/Status/StatusBorder";
import { getCleanName } from "@/shared/utils/accountConfig";

const ROLE_DISPLAY_MAX = 28;

const getContactName = (contact) =>
  getCleanName(contact?.contact_name || contact?.name || "");

const getContactRole = (contact) =>
  (contact?.designation || contact?.role || contact?.relationship_type || "").trim();

const getContactImage = (contact) =>
  contact?.avatar_url || contact?.profile_pic || contact?.image_url || "";

const shortenRole = (role, maxLength = ROLE_DISPLAY_MAX) => {
  if (!role || role.length <= maxLength) return role;
  return `${role.slice(0, maxLength).trimEnd()}…`;
};

const formatPersonPart = (contact, { fullRole = false } = {}) => {
  const name = getContactName(contact);
  const role = getContactRole(contact);
  if (!name) return "";
  if (!role) return name;
  const displayRole = fullRole ? role : shortenRole(role);
  return `${name} (${displayRole})`;
};

const joinPeopleParts = (parts) => {
  if (parts.length <= 1) return parts[0] || "";
  if (parts.length === 2) return `${parts[0]} & ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")} & ${parts[parts.length - 1]}`;
};

const formatPeopleInContactLabel = (people = [], { fullRole = false } = {}) => {
  const parts = people
    .map((contact) => formatPersonPart(contact, { fullRole }))
    .filter(Boolean);
  return joinPeopleParts(parts);
};

const PeopleInContactBlock = ({ contactName, people = [] }) => {
  if (!people?.length) return null;

  const label = formatPeopleInContactLabel(people);
  const fullLabel = formatPeopleInContactLabel(people, { fullRole: true });

  return (
    <div className="flex flex-col gap-2.5 items-start max-w-full">
      <h5 className="text-xs font-semibold text-gray-700">
        People in Contact with {contactName}
      </h5>
      <div className="flex gap-4 items-start max-w-full">
        {people.map((contact, index) => (
          <div
            className={`flex shrink-0 items-center ${index === 0 ? "ml-0" : "-ml-3.5"}`}
            key={`${getContactName(contact) || "contact"}-${index}`}
          >
            <StatusBorder
              number={contact?.engagement_score}
              grayborderColor={true}
            >
              <CommonAvatar
                name={getContactName(contact)}
                email={getContactRole(contact)}
                imageUrl={getContactImage(contact)}
                size="md"
              />
            </StatusBorder>
          </div>
        ))}
        <div
          className="min-w-0 flex-1 font-medium text-xs text-gray-500 leading-snug break-words"
          title={fullLabel !== label ? fullLabel : undefined}
        >
          {label}
        </div>
      </div>
    </div>
  );
};

export default PeopleInContactBlock;
